import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import LoadSpinner from "../../../utilities/LoadSpinner";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GavelIcon from "@mui/icons-material/Gavel";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_compliance_audit } from "../../../constants";
import { formatDateReadable } from "../requests/utilities/helpers";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  maxHeight: "85vh",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
};

function LaborComplianceAuditModal(props) {
  const { open, onClose, homeStore, selectedPayPeriodDates } = props;

  const [auditData, setAuditData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    if (open && homeStore && selectedPayPeriodDates?.length >= 1) {
      fetchAuditData();
    }
  }, [open]);

  const fetchAuditData = async () => {
    setIsLoading(true);
    setAuditData(null);
    try {
      const res = await getRightworkAPI(API_get_compliance_audit, {
        store_id: homeStore,
        date: selectedPayPeriodDates[0],
      });
      if (res.status === 200) {
        setAuditData(res.data?.redux?.payload);
      }
    } catch (error) {
      console.error("Error fetching compliance audit:", error);
    }
    setIsLoading(false);
  };

  const toggleDescription = (regType) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [regType]: !prev[regType],
    }));
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatViolationDate = (dateStr) => {
    if (!dateStr) return "-";
    const parts = dateStr.split("-");
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">
              Labor Compliance Audit
            </h2>
            {selectedPayPeriodDates?.length >= 2 && (
              <p className="mt-1 text-sm text-slate-500">
                {formatDateReadable(selectedPayPeriodDates[0])} to{" "}
                {formatDateReadable(selectedPayPeriodDates[1])}
              </p>
            )}
          </div>
          <div
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"
            onClick={onClose}
          >
            <CloseIcon style={{ fontSize: "18px" }} />
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <LoadSpinner />
          </div>
        )}

        {!isLoading && auditData && auditData.total_violations === 0 && (
          <div className="flex flex-col items-center py-20">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <GavelIcon style={{ color: "#059669", fontSize: "24px" }} />
            </div>
            <p className="mt-4 text-slate-900 font-medium">
              No labor law violations found
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {auditData.skipped_regulation_types?.length > 0
                ? "No violations found for the regulation types that were audited."
                : "All time entries for this pay period are compliant."}
            </p>
          </div>
        )}

        {!isLoading && auditData && auditData.total_violations > 0 && (
          <div className="mt-6">
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                {auditData.total_violations} labor law violation
                {auditData.total_violations !== 1 ? "s" : ""} found
              </p>
            </div>

            <div className="mt-6 space-y-6">
              {auditData.groups.map((group) => (
                <div
                  key={group.regulation_type}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                >
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {group.regulation_type_name}
                      </h3>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                        {group.violation_count} violation
                        {group.violation_count !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {group.legal_citation && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded">
                          {group.legal_citation}
                        </span>
                      </div>
                    )}

                    {group.legal_description && (
                      <div className="mt-2">
                        <div
                          className="flex items-center text-xs text-slate-500 cursor-pointer hover:text-slate-700"
                          onClick={() =>
                            toggleDescription(group.regulation_type)
                          }
                        >
                          <span>Legal description</span>
                          {expandedDescriptions[group.regulation_type] ? (
                            <ExpandLessIcon
                              style={{ fontSize: "16px", marginLeft: "2px" }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              style={{ fontSize: "16px", marginLeft: "2px" }}
                            />
                          )}
                        </div>
                        {expandedDescriptions[group.regulation_type] && (
                          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                            {group.legal_description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="px-4 py-2 text-left text-xs text-slate-500 font-medium">
                          Employee
                        </th>
                        <th className="px-4 py-2 text-left text-xs text-slate-500 font-medium">
                          Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs text-slate-500 font-medium">
                          Hours
                        </th>
                        <th className="px-4 py-2 text-left text-xs text-slate-500 font-medium">
                          Violation Detail
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {group.violations.map((v, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2 text-sm text-slate-900">
                            {v.employee_name}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-600">
                            {v.shift_count != null
                              ? `${v.shift_count} shift${v.shift_count !== 1 ? "s" : ""}`
                              : formatViolationDate(v.date)}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-600">
                            {v.total_hours != null
                              ? v.total_hours.toFixed(2)
                              : v.payable_hours != null
                                ? v.payable_hours.toFixed(2)
                                : "-"}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-600">
                            {v.violation_detail}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && auditData?.skipped_regulation_types?.length > 0 && (
          <div className="mt-6 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              Some regulation types could not be audited from time entry data:{" "}
              {auditData.skipped_regulation_types
                .map((s) => s.regulation_type_name)
                .join(", ")}
            </p>
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default LaborComplianceAuditModal;
